import type { MaybeRefOrGetter } from 'vue';
import { computed, readonly, ref, toValue, watch } from 'vue';

interface GridNavigationBehavior {
	wrapHorizontal?: MaybeRefOrGetter<boolean>;
	disabled?: MaybeRefOrGetter<boolean>;
	onActivate?: (event: KeyboardEvent) => void;
	onSelect?: (event: KeyboardEvent) => void;
}

interface UniformGrid {
	columns: MaybeRefOrGetter<number>;
	total: MaybeRefOrGetter<number>;
	rows?: never;
}

interface RaggedGrid {
	rows: MaybeRefOrGetter<readonly number[]>;
	columns?: never;
	total?: never;
}

export type UseGridNavigationOptions = GridNavigationBehavior & (UniformGrid | RaggedGrid);

type GridNavigationDirection = 'left' | 'right' | 'up' | 'down';

function isUniformOptions(options: UseGridNavigationOptions): options is GridNavigationBehavior & UniformGrid {
	return options.rows === undefined;
}

export function useGridNavigation(options: UseGridNavigationOptions) {
	const wrapHorizontal = computed(() => toValue(options.wrapHorizontal) ?? true);
	const isDisabled = computed(() => toValue(options.disabled) ?? false);

	const rowLengths = computed<number[]>(() => {
		if (!isUniformOptions(options)) {
			const lengths: number[] = [];

			for (const length of toValue(options.rows)) {
				lengths.push(Math.max(0, Math.floor(length)));
			}

			return lengths;
		}

		const columns = Math.max(1, Math.floor(toValue(options.columns)));
		const total = Math.max(0, Math.floor(toValue(options.total)));
		const lengths: number[] = [];

		for (let remaining = total; remaining > 0; remaining -= columns) {
			lengths.push(Math.min(columns, remaining));
		}

		return lengths;
	});

	const rowStarts = computed<number[]>(() => {
		const starts: number[] = [];
		let acc = 0;

		for (const length of rowLengths.value) {
			starts.push(acc);
			acc += length;
		}

		return starts;
	});

	const total = computed(() => {
		if (isUniformOptions(options)) return Math.max(0, Math.floor(toValue(options.total)));
		return rowLengths.value.reduce((sum, length) => sum + length, 0);
	});

	const focusedIndex = ref(-1);
	const elements = new Map<number, HTMLElement>();

	const tabbableIndex = computed(() => Math.max(focusedIndex.value, 0));

	watch(focusedIndex, (index) => {
		const element = elements.get(index);
		element?.focus();
		element?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
	});

	watch(total, (newTotal) => {
		if (focusedIndex.value >= newTotal) {
			focusedIndex.value = -1;
		}
	});

	function registerItem(index: number, element: HTMLElement | null) {
		if (element) {
			elements.set(index, element);
		}
		else {
			elements.delete(index);
		}
	}

	function setFocused(index: number) {
		if (index < 0 || index >= total.value) {
			focusedIndex.value = -1;
			return;
		}

		focusedIndex.value = index;
	}

	function handleArrowLeft(_event: KeyboardEvent) {
		navigate('left');
	}

	function handleArrowRight(_event: KeyboardEvent) {
		navigate('right');
	}

	function handleArrowUp(_event: KeyboardEvent) {
		navigate('up');
	}

	function handleArrowDown(_event: KeyboardEvent) {
		navigate('down');
	}

	function handleActivate(event: KeyboardEvent) {
		if (isDisabled.value) return;
		options.onActivate?.(event);
	}

	function handleSelect(event: KeyboardEvent) {
		if (isDisabled.value) return;
		options.onSelect?.(event);
	}

	function navigate(direction: GridNavigationDirection) {
		if (isDisabled.value || total.value === 0) return;

		const next = getNextIndex(direction);

		if (next === null) return;

		focusedIndex.value = next;
	}

	function rowColForIndex(index: number): { row: number; col: number } | null {
		const lengths = rowLengths.value;
		const starts = rowStarts.value;

		const row = lengths.findLastIndex((length, idx) => length > 0 && index >= starts[idx]!);
		return row === -1 ? null : { row, col: index - starts[row]! };
	}

	function firstNonEmptyRow(): number {
		return rowLengths.value.findIndex((length) => length > 0);
	}

	function nextNonEmptyRow(from: number): number {
		return rowLengths.value.findIndex((length, row) => row > from && length > 0);
	}

	function prevNonEmptyRow(from: number): number {
		return rowLengths.value.findLastIndex((length, row) => row < from && length > 0);
	}

	function getNextIndex(direction: GridNavigationDirection): number | null {
		if (isUniformOptions(options)) return getNextUniformIndex(options, direction);

		const current = focusedIndex.value;

		if (current < 0) {
			const first = firstNonEmptyRow();
			return first === -1 ? null : rowStarts.value[first]!;
		}

		const position = rowColForIndex(current);
		if (!position) return null;

		const { row, col } = position;
		const lengths = rowLengths.value;
		const starts = rowStarts.value;

		switch (direction) {
			case 'right': {
				if (col + 1 < lengths[row]!) {
					return current + 1;
				}

				if (!wrapHorizontal.value) return null;

				const next = nextNonEmptyRow(row);
				return next === -1 ? null : starts[next]!;
			}
			case 'left': {
				if (col > 0) {
					return current - 1;
				}

				if (!wrapHorizontal.value) return null;

				const prev = prevNonEmptyRow(row);
				return prev === -1 ? null : starts[prev]! + lengths[prev]! - 1;
			}
			case 'down': {
				const next = nextNonEmptyRow(row);
				if (next === -1) return null;

				const targetCol = Math.min(col, lengths[next]! - 1);
				return starts[next]! + targetCol;
			}
			case 'up': {
				const prev = prevNonEmptyRow(row);
				if (prev === -1) return null;

				const targetCol = Math.min(col, lengths[prev]! - 1);
				return starts[prev]! + targetCol;
			}
		}
	}

	function getNextUniformIndex(options: GridNavigationBehavior & UniformGrid, direction: GridNavigationDirection): number | null {
		const current = focusedIndex.value;
		const itemCount = total.value;
		const columnCount = Math.max(1, Math.floor(toValue(options.columns)));

		if (current < 0) return 0;

		const col = current % columnCount;

		switch (direction) {
			case 'right':
				if (current + 1 >= itemCount || (col + 1 === columnCount && !wrapHorizontal.value)) return null;
				return current + 1;
			case 'left':
				if (current === 0 || (col === 0 && !wrapHorizontal.value)) return null;
				return current - 1;
			case 'down': {
				const nextRowStart = (Math.floor(current / columnCount) + 1) * columnCount;
				if (nextRowStart >= itemCount) return null;
				return Math.min(current + columnCount, itemCount - 1);
			}
			case 'up': {
				const next = current - columnCount;
				return next < 0 ? null : next;
			}
		}
	}

	return {
		focusedIndex: readonly(focusedIndex),
		tabbableIndex,
		registerItem,
		setFocused,
		handleArrowLeft,
		handleArrowRight,
		handleArrowUp,
		handleArrowDown,
		handleActivate,
		handleSelect,
	};
}
